import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

type NativeAppBridgeRequestPayload = Record<string, unknown> | null | undefined;

type NativeAppBridgeResponse<TPayload = unknown> = {
  requestId?: string | null;
  success?: boolean | null;
  type?: string | null;
  payload?: TPayload | null;
  error?: string | null;
  target?: string | null;
};

export const PUBLIC_SESSION_SIGNED_APP_CONTEXT_BOOTSTRAP_TIMEOUT_MS = 5000;

export type NativeScreenProtectionState = {
  enabled: boolean;
};

export type NativeNotificationPermissionState = {
  platform: string;
  isAndroid: boolean;
  isIos: boolean;
  notificationsSupported: boolean;
  permissionStatus: string;
  granted: boolean;
  canRequestPermission: boolean;
};

export type NativeLocalAuthorizationResult = {
  authenticated: boolean;
  available: boolean;
  error?: string | null;
};

export type NativeSavedPublicSessionSignupEntry = {
  signupId: string;
  childId: string;
  label: string;
};

declare global {
  interface Window {
    ZalamoNativeApp?: {
      request?: (
        type: string,
        payload?: NativeAppBridgeRequestPayload,
      ) => Promise<unknown>;
      getNotificationPermissionState?: () => Promise<unknown>;
      requestNotificationPermission?: () => Promise<unknown>;
      notificationPermissionState?: NativeNotificationPermissionState | null;
      screenProtectionState?: NativeScreenProtectionState | null;
      screenProtectionEnabled?: boolean;
    };
    ZalamoPublicSessionAppContext?: {
      currentToken?: string | null;
      receive?: (token?: string | null) => void;
    };
    ZalamoPublicSessionNativeApp?: Window['ZalamoNativeApp'];
    ZalamoPublicSessionNativeBridge?: {
      postMessage?: (message: string) => void;
    };
    __zalamoNativeAppWindowMessageListener?: EventListener | null;
    __zalamoNotificationPermissionState?: NativeNotificationPermissionState | null;
    __zalamoScreenProtectionState?: NativeScreenProtectionState | null;
    __zalamoSignedAppContextToken?: string | null;
  }
}

@Injectable({
  providedIn: 'root',
})
export class PublicSessionNativeAppService {
  private static readonly EXPIRATION_GRACE_SECONDS = 15;

  private token: string | null = null;
  private pendingNativeTokenRequest: Promise<string | null> | null = null;
  private nativeRequestCounter = 0;
  private readonly pendingNativeRequests = new Map<
    string,
    {
      type: string;
      createdAt: number;
      timeoutMs: number;
      resolve: (payload: unknown) => void;
      reject: (error: Error) => void;
      timeoutId: number;
    }
  >();
  private readonly timedOutNativeRequests = new Map<
    string,
    {
      type: string;
      createdAt: number;
      timeoutMs: number;
    }
  >();

  readonly token$ = new BehaviorSubject<string | null>(null);
  private readonly notificationPermissionStateSubject =
    new BehaviorSubject<NativeNotificationPermissionState | null>(null);
  readonly notificationPermissionState$ =
    this.notificationPermissionStateSubject.asObservable();
  private readonly screenProtectionStateSubject =
    new BehaviorSubject<NativeScreenProtectionState | null>(null);
  readonly screenProtectionState$ =
    this.screenProtectionStateSubject.asObservable();

  constructor(private readonly ngZone: NgZone) {
    if (typeof window === 'undefined') {
      return;
    }

    const stagedToken = `${window.__zalamoSignedAppContextToken || ''}`.trim();
    const stagedNotificationPermissionState =
      this.normalizeNotificationPermissionState(
        window.__zalamoNotificationPermissionState,
      ) ||
      this.normalizeNotificationPermissionState(
        window.ZalamoNativeApp?.notificationPermissionState,
      );
    const stagedScreenProtectionState =
      this.normalizeScreenProtectionState(
        window.__zalamoScreenProtectionState,
      ) ||
      this.normalizeScreenProtectionState(
        window.ZalamoNativeApp?.screenProtectionState,
      );

    this.updateToken(stagedToken || null);
    this.updateNotificationPermissionState(stagedNotificationPermissionState);
    this.updateScreenProtectionState(stagedScreenProtectionState);

    window.addEventListener(
      'message',
      this.handleNativeBridgeWindowMessage as EventListener,
    );
    window.addEventListener(
      'zalamoNativeAppResponse',
      this.handleNativeBridgeResponseEvent as EventListener,
    );
    window.addEventListener(
      'zalamoNotificationPermissionState',
      this.handleNotificationPermissionStateEvent as EventListener,
    );
    window.addEventListener(
      'zalamoScreenProtectionState',
      this.handleScreenProtectionStateEvent as EventListener,
    );
    window.addEventListener(
      'zalamoSignedAppContext',
      this.handleSignedAppContextEvent as EventListener,
    );
  }

  getToken() {
    return this.token;
  }

  getSignedAppContextDeviceId(options?: {
    token?: string | null;
    requireAttested?: boolean;
  }) {
    const token = options?.token ?? this.token;
    if (
      !this.hasValidToken({
        token,
        requireAttested: options?.requireAttested,
      })
    ) {
      return null;
    }

    const deviceId = `${this.getTokenPayload(token)?.deviceId || ''}`.trim();
    return deviceId || null;
  }

  hasAttestedToken(token = this.token) {
    return this.getTokenPayload(token)?.attestedDeviceId === true;
  }

  hasValidToken(options?: {
    token?: string | null;
    requireAttested?: boolean;
  }) {
    const token = options?.token ?? this.token;
    const payload = this.getTokenPayload(token);
    if (!payload) {
      return false;
    }

    if (
      options?.requireAttested === true &&
      payload.attestedDeviceId !== true
    ) {
      return false;
    }

    const expiresAt = Number(payload.exp || 0);
    if (!expiresAt) {
      return false;
    }

    if (!`${payload.deviceId || ''}`.trim()) {
      return false;
    }

    return (
      expiresAt >
      Math.floor(Date.now() / 1000) +
        PublicSessionNativeAppService.EXPIRATION_GRACE_SECONDS
    );
  }

  hasBindableUserToken(options?: {
    token?: string | null;
    requireAttested?: boolean;
  }) {
    const token = options?.token ?? this.token;
    const payload = this.getTokenPayload(token);
    const mobileUserId = `${payload?.mobileUserId || ''}`.trim();

    if (!mobileUserId) {
      return false;
    }

    return this.hasValidToken({
      token,
      requireAttested: options?.requireAttested,
    });
  }

  getScreenProtectionState() {
    return this.screenProtectionStateSubject.value;
  }

  isLoadedByNativeApp() {
    return this.hasNativeBridge();
  }

  shouldExpectNativeAppBridge() {
    if (typeof window === 'undefined') {
      return false;
    }

    if (this.hasNativeBridge()) {
      return true;
    }

    try {
      const params = new URLSearchParams(window.location.search || '');
      return params.get('zalamo-native-app') === '1';
    } catch (error) {
      return false;
    }
  }

  waitForNativeAppBridge(timeoutMs = 0) {
    return this.waitForNativeBridge(Math.max(0, Number(timeoutMs ?? 0) || 0));
  }

  async prepareSignedAppContextTokenForBootstrap(options?: {
    timeoutMs?: number;
    requireAttested?: boolean;
  }) {
    const timeoutMs = Math.max(
      0,
      Number(
        options?.timeoutMs ??
          PUBLIC_SESSION_SIGNED_APP_CONTEXT_BOOTSTRAP_TIMEOUT_MS,
      ) || 0,
    );
    const requireAttested = options?.requireAttested === true;
    const startedAt = Date.now();

    if (
      !this.shouldExpectNativeAppBridge() ||
      this.hasValidToken({ requireAttested })
    ) {
      console.log(
        `[public-session-native-app] prepareSignedAppContextTokenForBootstrap result=skip durationMs=${
          Date.now() - startedAt
        } timeoutMs=${timeoutMs} requireAttested=${requireAttested} expectBridge=${this.shouldExpectNativeAppBridge()} hasValidToken=${this.hasValidToken(
          { requireAttested },
        )}`,
      );
      return this.getToken();
    }

    const bridgeReady = await this.waitForNativeAppBridge(timeoutMs);
    console.log(
      `[public-session-native-app] prepareSignedAppContextTokenForBootstrap bridgeReady=${bridgeReady} durationMs=${
        Date.now() - startedAt
      } timeoutMs=${timeoutMs} requireAttested=${requireAttested}`,
    );

    const token = await this.requestNativeToken({
      timeoutMs,
      requireAttested,
    });
    console.log(
      `[public-session-native-app] prepareSignedAppContextTokenForBootstrap result=completed durationMs=${
        Date.now() - startedAt
      } timeoutMs=${timeoutMs} requireAttested=${requireAttested} tokenPresent=${!!`${
        token || ''
      }`.trim()} attested=${this.hasAttestedToken(token)}`,
    );
    return token;
  }

  async requestNativeToken(options?: {
    timeoutMs?: number;
    force?: boolean;
    requireAttested?: boolean;
  }) {
    const force = options?.force === true;
    const requireAttested = options?.requireAttested === true;
    const timeoutMs = Math.max(
      0,
      Number(
        options?.timeoutMs ??
          PUBLIC_SESSION_SIGNED_APP_CONTEXT_BOOTSTRAP_TIMEOUT_MS,
      ) || 0,
    );
    const startedAt = Date.now();

    if (
      !force &&
      this.hasValidToken({
        requireAttested,
      })
    ) {
      console.log(
        `[public-session-native-app] requestNativeToken result=cache-hit durationMs=${
          Date.now() - startedAt
        } timeoutMs=${timeoutMs} requireAttested=${requireAttested} force=${force}`,
      );
      return this.token;
    }

    if (typeof window === 'undefined') {
      return null;
    }

    if (this.pendingNativeTokenRequest) {
      console.log(
        `[public-session-native-app] requestNativeToken result=join-inflight durationMs=${
          Date.now() - startedAt
        } timeoutMs=${timeoutMs} requireAttested=${requireAttested} force=${force}`,
      );
      return this.pendingNativeTokenRequest;
    }

    this.pendingNativeTokenRequest = (async () => {
      const response = await this.requestNativeBridgePayload<{
        token?: string | null;
      }>(
        'requestSignedAppContext',
        {
          force,
          requireAttested,
        },
        {
          timeoutMs,
        },
      );

      const requestedToken =
        `${response?.token || this.token || ''}`.trim() || null;
      if (requestedToken !== this.token) {
        this.updateToken(requestedToken);
      }

      if (
        requireAttested &&
        !this.hasValidToken({
          token: requestedToken,
          requireAttested: true,
        })
      ) {
        this.updateToken(null);
        console.log(
          `[public-session-native-app] requestNativeToken result=non-attested durationMs=${
            Date.now() - startedAt
          } timeoutMs=${timeoutMs} requireAttested=${requireAttested} force=${force}`,
        );
        return null;
      }

      console.log(
        `[public-session-native-app] requestNativeToken result=completed durationMs=${
          Date.now() - startedAt
        } timeoutMs=${timeoutMs} requireAttested=${requireAttested} force=${force} tokenPresent=${!!`${
          requestedToken || ''
        }`.trim()} attested=${this.hasAttestedToken(requestedToken)}`,
      );
      return requestedToken;
    })().finally(() => {
      this.pendingNativeTokenRequest = null;
    });

    return this.pendingNativeTokenRequest;
  }

  requestSignedAppContextToken(options?: {
    timeoutMs?: number;
    force?: boolean;
    requireAttested?: boolean;
  }) {
    return this.requestNativeToken(options);
  }

  async getNotificationPermissionState(options?: { timeoutMs?: number }) {
    if (typeof window === 'undefined') {
      return null;
    }

    const timeoutMs = Math.max(0, Number(options?.timeoutMs ?? 0) || 0);
    if (this.shouldExpectNativeAppBridge()) {
      const state = this.normalizeNotificationPermissionState(
        await this.requestNativeBridgePayload<NativeNotificationPermissionState>(
          'getNotificationPermissionState',
          null,
          {
            timeoutMs,
          },
        ),
      );

      if (state) {
        this.updateNotificationPermissionState(state);
        return state;
      }
    }

    return this.getCachedNotificationPermissionState();
  }

  async requestNotificationPermission(options?: { timeoutMs?: number }) {
    if (typeof window === 'undefined') {
      return null;
    }

    const timeoutMs = Math.max(0, Number(options?.timeoutMs ?? 0) || 0);
    const state = this.normalizeNotificationPermissionState(
      await this.requestNativeBridgePayload<NativeNotificationPermissionState>(
        'requestNotificationPermission',
        null,
        {
          timeoutMs,
        },
      ),
    );

    if (state) {
      this.updateNotificationPermissionState(state);
      return state;
    }

    return this.getCachedNotificationPermissionState();
  }

  async requestLocalAuthorization(options?: {
    timeoutMs?: number;
    reason?: string | null;
  }): Promise<NativeLocalAuthorizationResult> {
    const response = await this.requestNativeBridgePayload<{
      authenticated?: boolean | null;
      available?: boolean | null;
      error?: string | null;
    }>(
      'requestLocalAuthentication',
      {
        reason: `${options?.reason || ''}`.trim() || null,
      },
      {
        timeoutMs: Math.max(0, Number(options?.timeoutMs ?? 20000) || 0),
      },
    );

    if (!response) {
      return {
        authenticated: false,
        available: false,
        error: 'Brak połączenia z aplikacją.',
      };
    }

    return {
      authenticated: response.authenticated === true,
      available: response.available !== false,
      error: `${response.error || ''}`.trim() || null,
    };
  }

  async syncSavedPublicSessionSignups(options: {
    signupId: string;
    entries: NativeSavedPublicSessionSignupEntry[];
    timeoutMs?: number;
  }) {
    const signupId = `${options?.signupId || ''}`.trim();
    const entries = (options?.entries || [])
      .map((entry) => ({
        signupId: `${entry?.signupId || signupId}`.trim(),
        childId: `${entry?.childId || ''}`.trim(),
        label: `${entry?.label || ''}`.trim(),
      }))
      .filter((entry) => !!entry.signupId && !!entry.childId);

    if (!signupId) {
      return null;
    }

    return this.requestNativeBridgePayload<{ count?: number | null }>(
      'syncSavedPublicSessionSignups',
      {
        signupId,
        entries,
      },
      {
        timeoutMs: Math.max(0, Number(options?.timeoutMs ?? 1500) || 0),
      },
    );
  }

  async removeSavedPublicSessionSignup(options: {
    signupId: string;
    childId?: string | null;
    timeoutMs?: number;
  }) {
    const signupId = `${options?.signupId || ''}`.trim();
    if (!signupId) {
      return null;
    }

    const childId = `${options?.childId || ''}`.trim();

    return this.requestNativeBridgePayload<{ count?: number | null }>(
      'removeSavedPublicSessionSignup',
      {
        signupId,
        childId: childId || null,
      },
      {
        timeoutMs: Math.max(0, Number(options?.timeoutMs ?? 1200) || 0),
      },
    );
  }

  async requestScreenProtectionState(options?: { timeoutMs?: number }) {
    const state = this.normalizeScreenProtectionState(
      await this.requestNativeBridgePayload<NativeScreenProtectionState>(
        'getScreenProtectionState',
        null,
        {
          timeoutMs: Math.max(0, Number(options?.timeoutMs ?? 800) || 0),
        },
      ),
    );

    if (state) {
      this.updateScreenProtectionState(state);
      return state;
    }

    return this.screenProtectionStateSubject.value;
  }

  async setScreenProtection(
    enabled: boolean,
    options?: { timeoutMs?: number },
  ) {
    const state = this.normalizeScreenProtectionState(
      await this.requestNativeBridgePayload<NativeScreenProtectionState>(
        'setScreenProtection',
        {
          enabled: enabled === true,
        },
        {
          timeoutMs: Math.max(0, Number(options?.timeoutMs ?? 800) || 0),
        },
      ),
    );

    const resolvedState = state || {
      enabled: enabled === true,
    };
    this.updateScreenProtectionState(resolvedState);
    return resolvedState;
  }

  enableScreenProtection(options?: { timeoutMs?: number }) {
    return this.setScreenProtection(true, options);
  }

  disableScreenProtection(options?: { timeoutMs?: number }) {
    return this.setScreenProtection(false, options);
  }

  private updateToken(token: string | null) {
    this.token = token;
    window.__zalamoSignedAppContextToken = this.token;
    window.ZalamoPublicSessionAppContext = {
      ...(window.ZalamoPublicSessionAppContext || {}),
      currentToken: this.token,
      receive: this.receiveSignedAppContext,
    };
    this.token$.next(this.token);
  }

  private getCachedNotificationPermissionState() {
    return this.normalizeNotificationPermissionState(
      window.__zalamoNotificationPermissionState ||
        window.ZalamoNativeApp?.notificationPermissionState,
    );
  }

  private updateNotificationPermissionState(
    state: NativeNotificationPermissionState | null,
  ) {
    if (typeof window === 'undefined') {
      return;
    }

    window.__zalamoNotificationPermissionState = state;

    if (window.ZalamoNativeApp) {
      window.ZalamoNativeApp.notificationPermissionState = state;
    }

    this.notificationPermissionStateSubject.next(state);
  }

  private updateScreenProtectionState(
    state: NativeScreenProtectionState | null,
  ) {
    if (typeof window === 'undefined') {
      return;
    }

    window.__zalamoScreenProtectionState = state;

    if (window.ZalamoNativeApp) {
      window.ZalamoNativeApp.screenProtectionState = state;
      window.ZalamoNativeApp.screenProtectionEnabled = state?.enabled === true;
    }

    this.screenProtectionStateSubject.next(state);
  }

  private readonly receiveSignedAppContext = (token?: string | null) => {
    const normalizedToken = `${token || ''}`.trim();
    this.ngZone.run(() => {
      this.updateToken(normalizedToken || null);
    });
  };

  private readonly handleSignedAppContextEvent = (
    event: CustomEvent<{ token?: string | null }>,
  ) => {
    this.receiveSignedAppContext(event?.detail?.token || null);
  };

  private readonly handleNotificationPermissionStateEvent = (
    event: CustomEvent<NativeNotificationPermissionState>,
  ) => {
    const state = this.normalizeNotificationPermissionState(event?.detail);
    if (!state) {
      return;
    }

    this.ngZone.run(() => {
      this.updateNotificationPermissionState(state);
    });
  };

  private readonly handleScreenProtectionStateEvent = (
    event: CustomEvent<NativeScreenProtectionState>,
  ) => {
    const state = this.normalizeScreenProtectionState(event?.detail);
    if (!state) {
      return;
    }

    this.ngZone.run(() => {
      this.updateScreenProtectionState(state);
    });
  };

  private readonly handleNativeBridgeResponseEvent = (
    event: CustomEvent<NativeAppBridgeResponse>,
  ) => {
    this.handleNativeBridgeResponse(event?.detail);
  };

  private readonly handleNativeBridgeWindowMessage = (
    event: MessageEvent<NativeAppBridgeResponse>,
  ) => {
    const response = event?.data;
    if (!response || response.target !== 'zalamo-native-app-response') {
      return;
    }

    this.handleNativeBridgeResponse(response);
  };

  private handleNativeBridgeResponse(
    response?: NativeAppBridgeResponse | null,
  ) {
    const requestId = `${response?.requestId || ''}`.trim();
    if (!requestId) {
      return;
    }

    const pendingRequest = this.pendingNativeRequests.get(requestId);
    if (!pendingRequest) {
      const timedOutRequest = this.timedOutNativeRequests.get(requestId);
      if (timedOutRequest) {
        this.timedOutNativeRequests.delete(requestId);
        console.warn(
          `[public-session-native-app] requestNativeBridgePayload result=late-response-ignored type=${
            timedOutRequest.type
          } durationMs=${Date.now() - timedOutRequest.createdAt} timeoutMs=${
            timedOutRequest.timeoutMs
          }`,
        );
      }
      return;
    }

    this.pendingNativeRequests.delete(requestId);
    this.timedOutNativeRequests.delete(requestId);
    window.clearTimeout(pendingRequest.timeoutId);

    if (response?.type === 'screenProtectionState') {
      const state = this.normalizeScreenProtectionState(response.payload);
      if (state) {
        this.updateScreenProtectionState(state);
      }
    }

    if (response?.type === 'notificationPermissionState') {
      const state = this.normalizeNotificationPermissionState(response.payload);
      if (state) {
        this.updateNotificationPermissionState(state);
      }
    }

    if (response?.type === 'signedAppContext') {
      const tokenPayload = this.asRecord(response.payload);
      const token = `${tokenPayload?.token || ''}`.trim();
      if (token || tokenPayload?.token === null) {
        this.updateToken(token || null);
      }
    }

    if (response?.success === false) {
      pendingRequest.reject(
        new Error(`${response.error || 'Native bridge request failed.'}`),
      );
      return;
    }

    pendingRequest.resolve(response?.payload ?? null);
  }

  private async requestNativeBridgePayload<TPayload = unknown>(
    type: string,
    payload?: NativeAppBridgeRequestPayload,
    options?: {
      timeoutMs?: number;
    },
  ): Promise<TPayload | null> {
    if (typeof window === 'undefined') {
      return null;
    }

    const timeoutMs = Math.max(
      0,
      Number(
        options?.timeoutMs ??
          PUBLIC_SESSION_SIGNED_APP_CONTEXT_BOOTSTRAP_TIMEOUT_MS,
      ) || 0,
    );
    const startedAt = Date.now();
    const bridgeReady = await this.waitForNativeBridge(timeoutMs);
    if (type === 'requestSignedAppContext') {
      console.log(
        `[public-session-native-app] requestNativeBridgePayload type=${type} stage=bridge-check bridgeReady=${bridgeReady} durationMs=${
          Date.now() - startedAt
        } timeoutMs=${timeoutMs}`,
      );
    }
    if (!bridgeReady) {
      return null;
    }

    const requestId = this.createRequestId(type);

    return new Promise<TPayload | null>((resolve, reject) => {
      const finish = (callback: () => void) => {
        const pendingRequest = this.pendingNativeRequests.get(requestId);
        if (pendingRequest) {
          this.pendingNativeRequests.delete(requestId);
          window.clearTimeout(pendingRequest.timeoutId);
        }
        callback();
      };

      const timeoutId = window.setTimeout(() => {
        finish(() => {
          this.timedOutNativeRequests.set(requestId, {
            type,
            createdAt: startedAt,
            timeoutMs,
          });
          if (type === 'requestSignedAppContext') {
            console.warn(
              `[public-session-native-app] requestNativeBridgePayload type=${type} result=timeout durationMs=${
                Date.now() - startedAt
              } timeoutMs=${timeoutMs}`,
            );
          }
          resolve(null);
        });
      }, timeoutMs || 50);

      this.pendingNativeRequests.set(requestId, {
        type,
        createdAt: startedAt,
        timeoutMs,
        resolve: (responsePayload) => {
          finish(() => {
            if (type === 'requestSignedAppContext') {
              console.log(
                `[public-session-native-app] requestNativeBridgePayload type=${type} result=completed durationMs=${
                  Date.now() - startedAt
                } timeoutMs=${timeoutMs}`,
              );
            }
            resolve((responsePayload as TPayload | null) ?? null);
          });
        },
        reject: (error) => {
          finish(() => {
            reject(error);
          });
        },
        timeoutId,
      });

      try {
        window.postMessage(
          {
            target: 'zalamo-native-app',
            type,
            requestId,
            payload:
              payload && typeof payload === 'object'
                ? payload
                : payload == null
                ? null
                : {
                    value: payload,
                  },
          },
          '*',
        );
      } catch (error) {
        finish(() => {
          reject(
            error instanceof Error
              ? error
              : new Error('Failed to post native bridge message.'),
          );
        });
      }
    });
  }

  private async waitForNativeBridge(timeoutMs: number) {
    if (typeof window === 'undefined') {
      return false;
    }

    if (this.hasNativeBridge()) {
      return true;
    }

    if (timeoutMs <= 0) {
      return false;
    }

    return new Promise<boolean>((resolve) => {
      let resolved = false;

      const finish = (value: boolean) => {
        if (resolved) {
          return;
        }

        resolved = true;
        window.removeEventListener(
          'zalamoNativeAppReady',
          onReady as EventListener,
        );
        window.clearTimeout(timeoutId);
        resolve(value);
      };

      const onReady = () => {
        finish(this.hasNativeBridge());
      };

      const timeoutId = window.setTimeout(() => {
        finish(this.hasNativeBridge());
      }, timeoutMs);

      window.addEventListener('zalamoNativeAppReady', onReady as EventListener);
    });
  }

  private hasNativeBridge() {
    if (typeof window === 'undefined') {
      return false;
    }

    return Boolean(
      typeof window.ZalamoPublicSessionNativeBridge?.postMessage ===
        'function' ||
        typeof window.ZalamoNativeApp?.request === 'function' ||
        typeof window.__zalamoNativeAppWindowMessageListener === 'function',
    );
  }

  private createRequestId(type: string) {
    this.nativeRequestCounter += 1;
    return `public-session-native-${type}-${
      this.nativeRequestCounter
    }-${Date.now()}`;
  }

  private normalizeScreenProtectionState(
    value: unknown,
  ): NativeScreenProtectionState | null {
    const payload = this.asRecord(value);
    if (typeof payload?.enabled !== 'boolean') {
      return null;
    }

    return {
      enabled: payload.enabled === true,
    };
  }

  private normalizeNotificationPermissionState(
    value: unknown,
  ): NativeNotificationPermissionState | null {
    const payload = this.asRecord(value);
    if (!payload) {
      return null;
    }

    const platform = `${payload.platform || ''}`.trim().toLowerCase();
    const permissionStatus = `${payload.permissionStatus || ''}`.trim();
    const granted = payload.granted === true;

    return {
      platform,
      isAndroid: payload.isAndroid === true || platform === 'android',
      isIos: payload.isIos === true || platform === 'ios',
      notificationsSupported: payload.notificationsSupported !== false,
      permissionStatus,
      granted,
      canRequestPermission: payload.canRequestPermission === true,
    };
  }

  private asRecord(value: unknown): Record<string, unknown> | null {
    return value && typeof value === 'object'
      ? (value as Record<string, unknown>)
      : null;
  }

  private getTokenPayload(token?: string | null): {
    attestedDeviceId?: boolean | null;
    deviceId?: string | null;
    exp?: number | null;
    mobileUserId?: string | null;
  } | null {
    const normalizedToken = `${token || ''}`.trim();
    if (!normalizedToken || typeof window === 'undefined') {
      return null;
    }

    const segments = normalizedToken.split('.');
    if (segments.length < 2) {
      return null;
    }

    try {
      const base64 = segments[1].replace(/-/g, '+').replace(/_/g, '/');
      const normalizedBase64 = `${base64}${'='.repeat(
        (4 - (base64.length % 4)) % 4,
      )}`;
      const decodedPayload = window.atob(normalizedBase64);
      return JSON.parse(decodedPayload);
    } catch (error) {
      return null;
    }
  }
}
